import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GymClassificationEditor, gymValidationMessage } from '../components/gym/GymClassificationEditor';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  ArrowLeft,
  Edit2,
  Trash2,
  Copy,
  Plus,
  Users,
  Clock,
  MapPin,
  Calendar,
  User,
  CheckCircle,
  XCircle,
  AlertTriangle,
  MinusCircle,
  X,
  GripVertical,
  Eraser,
  ClipboardList,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSport } from '../context/SportContext';

// Fallback static list — replaced at runtime by sport-aware list from useSport().
const SESSION_TYPES_FALLBACK = [
  { value: 'TRAINING', label: 'Training' },
  { value: 'MATCH', label: 'Match' },
  { value: 'GYM', label: 'Gym' },
  { value: 'RECOVERY', label: 'Recovery' },
  { value: 'VBT', label: 'VBT' },
  { value: 'RTP', label: 'RTP' },
  { value: 'OTHER', label: 'Other' },
];

const PROGRAM_TYPES = [
  { value: 'TEAM', label: 'Team Program' },
  { value: 'RTP', label: 'RTP Program' },
];

const ASSIGNMENT_STATUSES = [
  { value: 'ASSIGNED', label: 'Assigned', icon: Users, color: 'text-zinc-400' },
  { value: 'COMPLETED', label: 'Completed', icon: CheckCircle, color: 'text-emerald-400' },
  { value: 'PARTIAL', label: 'Partial', icon: AlertTriangle, color: 'text-amber-400' },
  { value: 'MISSED', label: 'Missed', icon: XCircle, color: 'text-rose-400' },
  { value: 'EXCUSED', label: 'Excused', icon: MinusCircle, color: 'text-zinc-500' },
];

const StatusBadge = ({ status }) => {
  const found = ASSIGNMENT_STATUSES.find(s => s.value === status);
  const Icon = found?.icon || Users;
  return (
    <span className={`flex items-center gap-1.5 text-xs font-medium ${found?.color || 'text-zinc-400'}`}>
      <Icon className="w-3.5 h-3.5" />
      {found?.label || status}
    </span>
  );
};

const SessionDetailPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { api, hasRole, isAdmin } = useAuth();
  const { t } = useLanguage();
  const { sessionTypes: sportSessionTypes } = useSport();
  const SESSION_TYPES = sportSessionTypes && sportSessionTypes.length > 0
    ? sportSessionTypes
    : SESSION_TYPES_FALLBACK;

  const [session, setSession] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [availableAthletes, setAvailableAthletes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Assign dialog
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedAthletes, setSelectedAthletes] = useState([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  // Duplicate dialog
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateDate, setDuplicateDate] = useState('');
  const [isDuplicating, setIsDuplicating] = useState(false);

  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const canEdit = hasRole(['ADMIN', 'SNC', 'PHYSIO']);
  const canDelete = hasRole(['ADMIN', 'SNC']);

  const fetchSession = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/sessions/${sessionId}`);
      setSession(response.data);
      setEditData(response.data);
    } catch (err) {
      setError('Failed to load session');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [api, sessionId]);

  const fetchAssignments = useCallback(async () => {
    try {
      const response = await api.get(`/sessions/${sessionId}/assignments`);
      setAssignments(response.data);
    } catch (err) {
      console.error('Failed to fetch assignments:', err);
    }
  }, [api, sessionId]);

  const fetchAvailableAthletes = useCallback(async () => {
    if (!session) return;
    try {
      const response = await api.get(`/athletes?team_id=${session.team_id}`);
      // Filter out already assigned athletes
      const assignedIds = new Set(assignments.map(a => a.athlete_id));
      setAvailableAthletes(response.data.filter(a => !assignedIds.has(a.id)));
    } catch (err) {
      console.error('Failed to fetch athletes:', err);
    }
  }, [api, session, assignments]);

  useEffect(() => {
    fetchSession();
    fetchAssignments();
  }, [fetchSession, fetchAssignments]);

  useEffect(() => {
    if (session) {
      fetchAvailableAthletes();
    }
  }, [session, assignments, fetchAvailableAthletes]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Normalize content_json: drop empty/incomplete blocks and clean exercises,
      // so an intentional "cleared" state persists as {blocks: [], notes: ""}.
      const rawCj = editData.content_json || {};
      const cleanBlocks = (rawCj.blocks || [])
        .map((b, i) => {
          const nb = {
            order: b.order || i + 1,
            name: (b.name || '').trim(),
            duration_min: b.duration_min || null,
          };
          const exs = (b.exercises || [])
            .map((ex) => ({
              name: (ex.name || '').trim(),
              sets: ex.sets || null,
              reps: ex.reps || '',
              duration_sec: ex.duration_sec || null,
              load: ex.load || '',
            }))
            .filter((ex) => ex.name);
          if (exs.length > 0) nb.exercises = exs;
          if (b.notes && b.notes.trim()) nb.notes = b.notes.trim();
          return nb;
        })
        .filter((b) => b.name);
      const cleanContentJson = {
        blocks: cleanBlocks,
        notes: (rawCj.notes || '').trim(),
      };
      await api.put(`/sessions/${sessionId}`, {
        title: editData.title,
        type: editData.type,
        program_type: editData.program_type,
        description: editData.description,
        duration_planned_min: editData.duration_planned_min,
        ...(['GYM', 'STRENGTH'].includes(editData.type) || session.gym_classification != null
          ? { gym_classification: ['GYM', 'STRENGTH'].includes(editData.type) ? editData.gym_classification ?? null : null } : {}),
        location: editData.location,
        content_json: cleanContentJson,
        start_time: editData.start_time || '',
      });
      toast.success(t('saveSuccess'));
      setIsEditing(false);
      fetchSession();
    } catch (err) {
      toast.error(gymValidationMessage(err, t('error')));
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  // ------- Structured content editor helpers (RTP + other structured sessions) -------
  const getEditBlocks = () => (editData.content_json && editData.content_json.blocks) || [];
  const getEditNotes = () => (editData.content_json && editData.content_json.notes) || '';

  const updateContentJson = (patch) => {
    setEditData({
      ...editData,
      content_json: {
        blocks: (editData.content_json && editData.content_json.blocks) || [],
        notes: (editData.content_json && editData.content_json.notes) || '',
        ...patch,
      },
    });
  };

  const addBlock = () => {
    const blocks = getEditBlocks();
    updateContentJson({
      blocks: [
        ...blocks,
        { order: blocks.length + 1, name: '', duration_min: null, exercises: [], notes: '' },
      ],
    });
  };

  const updateBlock = (idx, patch) => {
    const blocks = getEditBlocks().map((b, i) => (i === idx ? { ...b, ...patch } : b));
    updateContentJson({ blocks });
  };

  const removeBlock = (idx) => {
    const blocks = getEditBlocks()
      .filter((_, i) => i !== idx)
      .map((b, i) => ({ ...b, order: i + 1 }));
    updateContentJson({ blocks });
  };

  const addExercise = (blockIdx) => {
    const blocks = getEditBlocks().map((b, i) =>
      i === blockIdx
        ? { ...b, exercises: [...(b.exercises || []), { name: '', sets: null, reps: '' }] }
        : b
    );
    updateContentJson({ blocks });
  };

  const updateExercise = (blockIdx, exIdx, patch) => {
    const blocks = getEditBlocks().map((b, i) => {
      if (i !== blockIdx) return b;
      const exs = (b.exercises || []).map((ex, j) => (j === exIdx ? { ...ex, ...patch } : ex));
      return { ...b, exercises: exs };
    });
    updateContentJson({ blocks });
  };

  const removeExercise = (blockIdx, exIdx) => {
    const blocks = getEditBlocks().map((b, i) => {
      if (i !== blockIdx) return b;
      const exs = (b.exercises || []).filter((_, j) => j !== exIdx);
      return { ...b, exercises: exs };
    });
    updateContentJson({ blocks });
  };

  const clearAllStructuredContent = () => {
    setEditData({
      ...editData,
      content_json: { blocks: [], notes: '' },
    });
    toast.info(t('rtpContentCleared') || 'Structured content cleared. Save to persist.');
  };

  const handleBulkAssign = async () => {
    if (selectedAthletes.length === 0) return;
    setIsAssigning(true);
    try {
      await api.post('/session-assignments/bulk', {
        session_id: sessionId,
        athlete_ids: selectedAthletes,
      });
      toast.success(`Assigned ${selectedAthletes.length} athletes`);
      setShowAssignDialog(false);
      setSelectedAthletes([]);
      fetchAssignments();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
      console.error(err);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUpdateStatus = async (assignmentId, newStatus) => {
    try {
      await api.put(`/session-assignments/${assignmentId}`, { status: newStatus });
      toast.success('Status updated');
      fetchAssignments();
    } catch (err) {
      toast.error(t('error'));
      console.error(err);
    }
  };

  // ---- Bulk "Mark all as completed" ----
  const [showBulkCompleteDialog, setShowBulkCompleteDialog] = useState(false);
  const [isBulkCompleting, setIsBulkCompleting] = useState(false);
  const canBulkCompleteRole = !hasRole(['NUTRITIONIST', 'ATHLETE']);
  const canBulkComplete = canEdit && canBulkCompleteRole && assignments.length > 0;

  // iter_p05 — live completion counter (X/N completed)
  const completedCount = assignments.filter((a) => a.status === 'COMPLETED').length;

  const handleBulkMarkCompleted = async () => {
    if (isBulkCompleting) return; // Prevent double-click duplicates.
    setIsBulkCompleting(true);
    try {
      const res = await api.patch(
        `/sessions/${sessionId}/assignments/bulk-status`,
        { status: 'COMPLETED' }
      );
      const updated = res.data?.updated_count ?? assignments.length;
      toast.success(
        t(
          `Marked ${updated} athletes as completed`,
          `${updated} atleti impostati come Completato`
        )
      );
      setShowBulkCompleteDialog(false);
      fetchAssignments();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
      console.error(err);
    } finally {
      setIsBulkCompleting(false);
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    try {
      await api.delete(`/session-assignments/${assignmentId}`);
      toast.success('Athlete removed');
      fetchAssignments();
    } catch (err) {
      toast.error(t('error'));
      console.error(err);
    }
  };

  const handleDuplicate = async () => {
    if (!duplicateDate) return;
    setIsDuplicating(true);
    try {
      const response = await api.post(`/sessions/${sessionId}/duplicate?new_date=${duplicateDate}`);
      toast.success('Session duplicated');
      setShowDuplicateDialog(false);
      navigate(`/staff/planner/session/${response.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
      console.error(err);
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleDelete = async () => {
    if (isDeleting) return; // P0.5.13D-EXT — reject re-entry.
    setIsDeleting(true);
    try {
      await api.delete(`/sessions/${sessionId}`);
      toast.success(t('deleteSuccess'));
      navigate('/staff/planner');
    } catch (err) {
      // Backend returns dict `detail` for PLANNER_HAS_LINKED_LOAD 409;
      // render safely so the user sees the reason, not `[object Object]`.
      const d = err?.response?.data?.detail;
      const msg = typeof d === 'string'
        ? d
        : (d && typeof d === 'object' && d.message) || null;
      toast.error(msg || t('error'));
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleAthleteSelection = (athleteId) => {
    setSelectedAthletes(prev => 
      prev.includes(athleteId)
        ? prev.filter(id => id !== athleteId)
        : [...prev, athleteId]
    );
  };

  const selectAllAvailable = () => {
    const filtered = getFilteredAthletes();
    setSelectedAthletes(filtered.map(a => a.id));
  };

  const getFilteredAthletes = () => {
    if (statusFilter === 'all') return availableAthletes;
    return availableAthletes.filter(a => a.status === statusFilter);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-zinc-500">{t('loading')}</div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="text-center py-12">
        <p className="text-rose-400">{error || 'Session not found'}</p>
        <Button
          onClick={() => navigate('/staff/planner')}
          variant="outline"
          className="mt-4 border-zinc-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Planner
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          size="icon"
          className="text-zinc-400 hover:text-zinc-100 self-start shrink-0"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-zinc-100">{session.title}</h1>
            <span className={`px-2 py-1 text-xs font-semibold rounded-sm ${
              session.program_type === 'RTP' 
                ? 'bg-orange-500/20 text-orange-400'
                : 'bg-zinc-800 text-zinc-400'
            }`}>
              {session.program_type}
            </span>
          </div>
          <div className="flex items-center gap-4 text-sm text-zinc-500 mt-1 flex-wrap">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" /> {session.date}
            </span>
            {session.start_time && (
              <span className="flex items-center gap-1 text-amber-400/80">
                <Clock className="w-4 h-4" /> {session.start_time}
              </span>
            )}
            <span className="flex items-center gap-1">
              {session.team_code}
            </span>
            <span className="px-2 py-0.5 bg-zinc-800 rounded text-xs">{session.type}</span>
            {(session.source === 'PRESCRIPTION' || session.prescription_id) && (
              <Link
                to={`/staff/prescriptions/${session.prescription_id}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 border border-amber-500/40 text-amber-300 rounded text-xs hover:bg-amber-500/30"
                data-testid="session-source-prescription-link"
              >
                <ClipboardList className="w-3 h-3" /> From Prescription
              </Link>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canEdit && (
            <Button
              onClick={() => setShowDuplicateDialog(true)}
              variant="outline"
              size="sm"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              data-testid="duplicate-button"
            >
              <Copy className="w-4 h-4 mr-2" />
              Duplicate
            </Button>
          )}
          {canEdit && !isEditing && (
            <Button
              onClick={() => setIsEditing(true)}
              variant="outline"
              size="sm"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              data-testid="edit-button"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              {t('edit')}
            </Button>
          )}
          {canDelete && (
            <Button
              onClick={() => setShowDeleteDialog(true)}
              variant="outline"
              size="sm"
              className="border-rose-900/50 text-rose-400 hover:bg-rose-900/20"
              data-testid="delete-button"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Session Details */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
            <h2 className="text-lg font-semibold text-zinc-100 mb-4">
              {isEditing ? 'Edit Session' : 'Session Details'}
            </h2>

            {isEditing ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Title</Label>
                  <Input
                    value={editData.title || ''}
                    onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                    className="bg-zinc-950 border-zinc-800"
                    data-testid="edit-title"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Type</Label>
                    <Select
                      value={editData.type || ''}
                      onValueChange={(val) => setEditData({ ...editData, type: val })}
                    >
                      <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="edit-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {SESSION_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Program</Label>
                    <Select
                      value={editData.program_type || ''}
                      onValueChange={(val) => setEditData({ ...editData, program_type: val })}
                    >
                      <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="edit-program">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {PROGRAM_TYPES.map((p) => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Duration (min)</Label>
                    <Input
                      type="number"
                      value={editData.duration_planned_min ?? ''}
                      onChange={(e) => setEditData({ ...editData, duration_planned_min: e.target.value === '' ? null : parseInt(e.target.value) })}
                      className="bg-zinc-950 border-zinc-800"
                      data-testid="edit-duration"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Start Time (optional)</Label>
                    <Input
                      type="time"
                      value={editData.start_time || ''}
                      onChange={(e) => setEditData({ ...editData, start_time: e.target.value })}
                      className="bg-zinc-950 border-zinc-800"
                      data-testid="edit-start-time"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    {['GYM', 'STRENGTH'].includes(editData.type) && <GymClassificationEditor value={editData.gym_classification ?? null}
                      onChange={value => setEditData({ ...editData, gym_classification: value })}
                      duration={editData.duration_planned_min} prefix="planner-edit-gym" />}
                    <Label className="text-zinc-400">Location</Label>
                    <Input
                      value={editData.location || ''}
                      onChange={(e) => setEditData({ ...editData, location: e.target.value })}
                      className="bg-zinc-950 border-zinc-800"
                      data-testid="edit-location"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-400">Description</Label>
                  <Textarea
                    value={editData.description || ''}
                    onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 min-h-[100px]"
                    data-testid="edit-description"
                  />
                </div>

                {/* Structured Session Content Editor (RTP + structured sessions) */}
                <div
                  className="space-y-3 pt-4 border-t border-zinc-800"
                  data-testid="edit-structured-content"
                >
                  <div className="flex items-center justify-between">
                    <Label className="text-zinc-300 font-semibold">Structured Content</Label>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border-zinc-700 text-zinc-400 hover:text-orange-400 hover:border-orange-500/50 h-7 text-xs"
                        onClick={addBlock}
                        data-testid="edit-add-block"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add Block
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border-rose-900/50 text-rose-400 hover:bg-rose-900/20 h-7 text-xs"
                        onClick={clearAllStructuredContent}
                        data-testid="edit-clear-content"
                        disabled={
                          getEditBlocks().length === 0 && !getEditNotes()
                        }
                      >
                        <Eraser className="w-3 h-3 mr-1" /> Clear prescribed content
                      </Button>
                    </div>
                  </div>

                  {getEditBlocks().length === 0 && !getEditNotes() && (
                    <p
                      className="text-xs text-zinc-500 italic"
                      data-testid="edit-structured-empty"
                    >
                      No structured prescription. Use &quot;Add Block&quot; to add one, or leave empty.
                    </p>
                  )}

                  <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
                    {getEditBlocks().map((block, blockIdx) => (
                      <div
                        key={blockIdx}
                        className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 space-y-2"
                        data-testid={`edit-block-${blockIdx}`}
                      >
                        <div className="flex items-center gap-2">
                          <GripVertical className="w-3.5 h-3.5 text-zinc-600 flex-shrink-0" />
                          <span className="text-[10px] text-zinc-500 font-mono w-5">
                            {block.order || blockIdx + 1}
                          </span>
                          <Input
                            value={block.name || ''}
                            onChange={(e) =>
                              updateBlock(blockIdx, { name: e.target.value })
                            }
                            placeholder="Block name"
                            className="bg-zinc-900 border-zinc-700 h-7 text-sm flex-1"
                            data-testid={`edit-block-name-${blockIdx}`}
                          />
                          <Input
                            type="number"
                            value={block.duration_min || ''}
                            onChange={(e) =>
                              updateBlock(blockIdx, {
                                duration_min: parseInt(e.target.value) || null,
                              })
                            }
                            placeholder="min"
                            className="bg-zinc-900 border-zinc-700 h-7 text-sm w-16"
                            data-testid={`edit-block-duration-${blockIdx}`}
                          />
                          <span className="text-[10px] text-zinc-500">min</span>
                          <button
                            type="button"
                            onClick={() => removeBlock(blockIdx)}
                            className="p-1 text-zinc-600 hover:text-rose-400 transition-colors"
                            data-testid={`edit-remove-block-${blockIdx}`}
                            title="Remove block"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        {block.exercises && block.exercises.length > 0 && (
                          <div className="pl-8 space-y-1">
                            {block.exercises.map((ex, exIdx) => (
                              <div key={exIdx} className="flex items-center gap-2">
                                <Input
                                  value={ex.name || ''}
                                  onChange={(e) =>
                                    updateExercise(blockIdx, exIdx, { name: e.target.value })
                                  }
                                  placeholder="Exercise name"
                                  className="bg-zinc-900 border-zinc-700 h-6 text-xs flex-1"
                                  data-testid={`edit-ex-name-${blockIdx}-${exIdx}`}
                                />
                                <Input
                                  value={ex.sets || ''}
                                  onChange={(e) =>
                                    updateExercise(blockIdx, exIdx, {
                                      sets: parseInt(e.target.value) || null,
                                    })
                                  }
                                  placeholder="Sets"
                                  className="bg-zinc-900 border-zinc-700 h-6 text-xs w-14"
                                  data-testid={`edit-ex-sets-${blockIdx}-${exIdx}`}
                                />
                                <span className="text-[10px] text-zinc-600">x</span>
                                <Input
                                  value={ex.reps || ''}
                                  onChange={(e) =>
                                    updateExercise(blockIdx, exIdx, { reps: e.target.value })
                                  }
                                  placeholder="Reps"
                                  className="bg-zinc-900 border-zinc-700 h-6 text-xs w-16"
                                  data-testid={`edit-ex-reps-${blockIdx}-${exIdx}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => removeExercise(blockIdx, exIdx)}
                                  className="p-0.5 text-zinc-600 hover:text-rose-400"
                                  data-testid={`edit-remove-ex-${blockIdx}-${exIdx}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => addExercise(blockIdx)}
                          className="ml-8 text-[10px] text-zinc-500 hover:text-orange-400 transition-colors"
                          data-testid={`edit-add-exercise-${blockIdx}`}
                        >
                          + Add exercise
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-zinc-400 text-xs">Session Notes</Label>
                    <Textarea
                      value={getEditNotes()}
                      onChange={(e) => updateContentJson({ notes: e.target.value })}
                      placeholder="Notes attached to the prescription (optional)"
                      className="bg-zinc-950 border-zinc-800 min-h-[40px] text-xs"
                      data-testid="edit-content-notes"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
                  <Button
                    onClick={() => { setIsEditing(false); setEditData(session); }}
                    variant="outline"
                    className="border-zinc-700 text-zinc-300"
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="bg-amber-500 text-zinc-950 hover:bg-amber-400 font-bold"
                    data-testid="save-button"
                  >
                    {isSaving ? t('loading') : t('save')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {['GYM', 'STRENGTH'].includes(session.type) && <GymClassificationEditor
                  value={session.gym_classification ?? null} duration={session.duration_planned_min}
                  prefix="planner-gym-view" readOnly />}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-zinc-800 rounded-sm flex items-center justify-center">
                      <Clock className="w-5 h-5 text-zinc-500" />
                    </div>
                    <div>
                      <div className="text-xs text-zinc-500 uppercase tracking-wider">Duration</div>
                      <div className="text-zinc-100">{session.duration_planned_min ?? '-'} min</div>
                    </div>
                  </div>
                  {session.start_time && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-500/10 rounded-sm flex items-center justify-center">
                        <Clock className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <div className="text-xs text-zinc-500 uppercase tracking-wider">Start Time</div>
                        <div className="text-zinc-100">{session.start_time}</div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-zinc-800 rounded-sm flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-zinc-500" />
                    </div>
                    <div>
                      <div className="text-xs text-zinc-500 uppercase tracking-wider">Location</div>
                      <div className="text-zinc-100">{session.location || '-'}</div>
                    </div>
                  </div>
                </div>
                {session.description && (
                  <div className="pt-4 border-t border-zinc-800">
                    <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Description</div>
                    <p
                      className="text-zinc-300 text-sm whitespace-pre-wrap break-words"
                      data-testid="session-description-display"
                    >
                      {session.description}
                    </p>
                  </div>
                )}
                {session.content_json && session.content_json.blocks && session.content_json.blocks.length > 0 && (
                  <div className="pt-4 border-t border-zinc-800">
                    <div className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Session Content</div>
                    <div className="space-y-3">
                      {session.content_json.blocks.map((block, idx) => (
                        <div key={idx} className="bg-zinc-950/50 border border-zinc-800 rounded-sm p-3">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-zinc-600 font-mono bg-zinc-800 rounded px-1.5 py-0.5">{block.order || idx + 1}</span>
                            <div className="font-medium text-zinc-200 text-sm">{block.name}</div>
                            {block.duration_min && (
                              <span className="text-xs text-zinc-500 ml-auto flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {block.duration_min} min
                              </span>
                            )}
                          </div>
                          {block.exercises && block.exercises.length > 0 && (
                            <ul className="mt-2 space-y-1 pl-6">
                              {block.exercises.map((ex, exIdx) => (
                                <li key={exIdx} className="text-xs text-zinc-400 flex items-start gap-1.5">
                                  <span className="text-zinc-600 mt-0.5">-</span>
                                  <span>
                                    {ex.name}
                                    {ex.sets && ex.reps && <span className="text-zinc-500 ml-1">({ex.sets}x{ex.reps})</span>}
                                    {ex.duration_sec && <span className="text-zinc-500 ml-1">({Math.round(ex.duration_sec / 60)} min)</span>}
                                    {ex.load && <span className="text-zinc-500 ml-1">@ {ex.load}</span>}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                          {block.notes && (
                            <p className="text-xs text-zinc-500 italic mt-1 pl-6 whitespace-pre-wrap break-words">{block.notes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                    {session.content_json.notes && (
                      <div className="mt-3 p-2 bg-amber-500/5 border border-amber-500/10 rounded-sm">
                        <span className="text-xs text-amber-400/80 font-medium">Notes: </span>
                        <span className="text-xs text-zinc-400 whitespace-pre-wrap break-words">{session.content_json.notes}</span>
                      </div>
                    )}
                    {session.content_json.goals && session.content_json.goals.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs text-zinc-500">Goals: </span>
                        <span className="text-xs text-zinc-400">{session.content_json.goals.join(', ')}</span>
                      </div>
                    )}
                    {session.content_json.equipment && session.content_json.equipment.length > 0 && (
                      <div className="mt-1">
                        <span className="text-xs text-zinc-500">Equipment: </span>
                        <span className="text-xs text-zinc-400">{session.content_json.equipment.join(', ')}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Assignments */}
        <div className="space-y-6">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm">
            <div className="p-4 border-b border-zinc-800 flex items-start sm:items-center justify-between gap-2 flex-wrap">
              <div className="flex flex-col">
                <h2 className="font-semibold text-zinc-100" data-testid="assignments-header">
                  {t('Assignments', 'Assegnazioni')} ({assignments.length})
                </h2>
                {assignments.length > 0 && (
                  <span
                    className="text-xs text-zinc-400 mt-0.5"
                    data-testid="assignments-completion-counter"
                  >
                    {completedCount}/{assignments.length} {t('completed', 'completati')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {canBulkComplete && (
                  <Button
                    onClick={() => setShowBulkCompleteDialog(true)}
                    size="sm"
                    className="h-11 sm:h-9 min-h-[44px] sm:min-h-0 bg-emerald-600 text-white hover:bg-emerald-500 border border-emerald-500/50"
                    data-testid="bulk-mark-completed-button"
                    title={t('Mark all as completed', 'Segna tutti come completato')}
                  >
                    <CheckCircle className="w-4 h-4 sm:mr-1.5" />
                    <span className="hidden sm:inline text-xs font-semibold">
                      {t('Mark all as completed', 'Segna tutti come completato')}
                    </span>
                    <span className="sm:hidden text-xs font-semibold ml-1.5">
                      {t('Complete all', 'Completa tutti')}
                    </span>
                  </Button>
                )}
                {canEdit && (
                  <Button
                    onClick={() => setShowAssignDialog(true)}
                    size="sm"
                    className="bg-amber-500 text-zinc-950 hover:bg-amber-400 h-11 sm:h-9 min-h-[44px] sm:min-h-0 min-w-[44px] sm:min-w-0"
                    data-testid="assign-athletes-button"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
            <div className="p-4 space-y-2 max-h-[500px] overflow-y-auto">
              {assignments.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-4">
                  {t('No athletes assigned', 'Nessun atleta assegnato')}
                </p>
              ) : (
                assignments.map((assign) => (
                  <div
                    key={assign.id}
                    className="flex flex-wrap items-center justify-between gap-2 p-3 bg-zinc-950/50 border border-zinc-800 rounded-sm"
                    data-testid={`assignment-row-${assign.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-8 h-8 shrink-0 bg-zinc-800 rounded-sm flex items-center justify-center text-zinc-500">
                        <User className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-zinc-100 truncate">{assign.athlete_name}</div>
                        <div className="text-xs text-zinc-500 truncate">{assign.athlete_position}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {canEdit ? (
                        <Select
                          value={assign.status}
                          onValueChange={(val) => handleUpdateStatus(assign.id, val)}
                        >
                          <SelectTrigger
                            className="w-[130px] h-11 sm:h-8 min-h-[44px] sm:min-h-0 bg-transparent border-zinc-700 text-xs"
                            data-testid={`status-${assign.id}`}
                          >
                            <StatusBadge status={assign.status} />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            {ASSIGNMENT_STATUSES.map((s) => (
                              <SelectItem key={s.value} value={s.value}>
                                <StatusBadge status={s.value} />
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <StatusBadge status={assign.status} />
                      )}
                      {canEdit && (
                        <button
                          onClick={() => handleRemoveAssignment(assign.id)}
                          className="p-2 sm:p-1 min-w-[44px] sm:min-w-0 min-h-[44px] sm:min-h-0 flex items-center justify-center text-zinc-500 hover:text-rose-400 transition-colors"
                          data-testid={`remove-${assign.id}`}
                          aria-label={t('Remove', 'Rimuovi')}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Assign Athletes Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Assign Athletes</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Select athletes from {session.team_code} to assign to this session
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[150px] bg-zinc-950 border-zinc-800" data-testid="athlete-status-filter">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="FULLTRAIN">Fit Only</SelectItem>
                  <SelectItem value="RTP">RTP Only</SelectItem>
                </SelectContent>
              </Select>
              <Button
                onClick={selectAllAvailable}
                variant="outline"
                size="sm"
                className="border-zinc-700 text-zinc-300"
                data-testid="select-all"
              >
                Select All ({getFilteredAthletes().length})
              </Button>
            </div>
            <div className="border border-zinc-800 rounded-sm max-h-[300px] overflow-y-auto">
              {getFilteredAthletes().length === 0 ? (
                <div className="p-4 text-center text-zinc-500 text-sm">
                  No available athletes
                </div>
              ) : (
                getFilteredAthletes().map((athlete) => (
                  <label
                    key={athlete.id}
                    className={`flex items-center gap-3 p-3 border-b border-zinc-800 last:border-b-0 cursor-pointer hover:bg-zinc-800/30 ${
                      selectedAthletes.includes(athlete.id) ? 'bg-amber-500/10' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedAthletes.includes(athlete.id)}
                      onChange={() => toggleAthleteSelection(athlete.id)}
                      className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-amber-500 focus:ring-amber-500"
                      data-testid={`select-athlete-${athlete.id}`}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-zinc-100">
                        {athlete.first_name} {athlete.last_name}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {athlete.primary_position} • #{athlete.jersey_number || '-'}
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 text-xs rounded ${
                      athlete.status === 'FULLTRAIN' 
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : athlete.status === 'RTP'
                        ? 'bg-amber-500/20 text-amber-400'
                        : 'bg-rose-500/20 text-rose-400'
                    }`}>
                      {athlete.status === 'FULLTRAIN' ? 'FIT' : athlete.status}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAssignDialog(false)}
              className="border-zinc-700 text-zinc-300"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleBulkAssign}
              disabled={isAssigning || selectedAthletes.length === 0}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400 font-bold"
              data-testid="confirm-assign"
            >
              {isAssigning ? t('loading') : `Assign ${selectedAthletes.length} Athletes`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Duplicate Session</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Create a copy of this session on a new date
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-zinc-400">New Date</Label>
            <Input
              type="date"
              value={duplicateDate}
              onChange={(e) => setDuplicateDate(e.target.value)}
              className="bg-zinc-950 border-zinc-800 mt-2"
              data-testid="duplicate-date"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDuplicateDialog(false)}
              className="border-zinc-700 text-zinc-300"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleDuplicate}
              disabled={isDuplicating || !duplicateDate}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400 font-bold"
              data-testid="confirm-duplicate"
            >
              {isDuplicating ? t('loading') : 'Duplicate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Bulk Mark-as-Completed Confirmation */}
      <AlertDialog open={showBulkCompleteDialog} onOpenChange={setShowBulkCompleteDialog}>
        <AlertDialogContent
          className="bg-zinc-900 border-zinc-800 max-w-[92vw] sm:max-w-md"
          data-testid="bulk-mark-completed-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-100">
              {t('Mark all as completed?', 'Segnare tutti come completato?')}
            </AlertDialogTitle>
            <AlertDialogDescription
              className="text-zinc-400"
              data-testid="bulk-mark-completed-message"
            >
              {t(
                `All ${assignments.length} assigned athletes will be set to Completed. You will still be able to change individual exceptions afterwards.`,
                `Tutti i ${assignments.length} atleti assegnati verranno impostati come Completato. Potrai modificare manualmente le eccezioni.`
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              data-testid="bulk-mark-completed-cancel"
            >
              {t('Cancel', 'Annulla')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkMarkCompleted}
              disabled={isBulkCompleting}
              className="bg-emerald-600 text-white hover:bg-emerald-500"
              data-testid="bulk-mark-completed-confirm"
            >
              {isBulkCompleting
                ? t('Loading...', 'Caricamento...')
                : t('Confirm', 'Conferma')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-100">Delete Session</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {t('confirmDelete')} All assignments will be marked as excused.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-rose-600 text-white hover:bg-rose-500"
              data-testid="confirm-delete"
            >
              {isDeleting ? t('loading') : t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SessionDetailPage;
